import { useCallback } from "react";
import { Button, Flex } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { type SubmitHandler, useForm } from "react-hook-form";

import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useGetApi, usePostApi } from "src/hooks";
import { ButtonsWrapper, PageContainer, SectionDivider } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { LayoutContainer, Section } from "../../../../styled/UserForm.styled";

const UnCodeAutomation = () => {
  const { post, isLoading } = usePostApi();

  const yachtid = getYachtId();

  type ILbItem = {
    lbid: string;
    enabled: boolean;
    parts?: ILbItem[];
  };

  const flattenLogbooks = useCallback((logbooks: any[]) => {
    const result: any[] = [];
    logbooks.forEach(lb => {
      result.push({
        lbid: lb.lbid,
        enabled:
          (lb.enabled ||
            (lb.parts && lb.parts.every((part: ILbItem) => part.enabled))) ??
          false,
      });
      if (lb.parts && lb.parts.length > 0) {
        lb.parts.forEach((part: ILbItem) => {
          result.push({ lbid: part.lbid, enabled: part.enabled ?? false });
        });
      }
    });
    return result;
  }, []);

  const listMapper = useCallback(
    (data: any) => ({
      list: flattenLogbooks(data?.result?.data) ?? [],
      selects: {
        logbooks: data?.result?.data.map((lb: any) => ({
          value: lb.lbid,
          label: lb.lbname,
          parts: lb.parts
            ? lb.parts.map((part: any) => ({
                value: part.lbid,
                label: part.lbname,
              }))
            : [],
        })),
      },
      initialValues: {
        logbooks:
          flattenLogbooks(data?.result?.data)
            .filter((lb: any) => lb.enabled)
            .map((lb: any) => lb.lbid) ?? [],
      },
    }),
    [],
  );

  const { data, isLoading: lbLoading } = useGetApi<any>({
    request: "get-uncode-logbooks",
    body: {
      params: { yachtid },
    },
    mapper: listMapper,
  });

  const { control, handleSubmit, trigger } = useForm<any>({
    values: data?.initialValues,
  });

  const submit: SubmitHandler<any> = async ({ logbooks }) => {
    for (const lb of data.list) {
      lb.enabled = logbooks.includes(lb.lbid);
    }
    await post({
      request: "save-uncode-logbooks",
      body: {
        params: {
          yachtid,
          list: data.list,
        },
      },
      onSuccess: () => {
        Notification("Success", `Logbooks updated successfully`, "green");
      },
    });
  };

  const onSubmit = async () => {
    const result = await trigger();
    if (result) {
      await handleSubmit(submit)();
    } else {
      Notification("Validation Error", "Fill in the required fields", "red");
    }
  };

  return (
    <PageContainer>
      <form>
        <Breadcrumbs>
          <ButtonsWrapper>
            <Button
              onClick={onSubmit}
              leftIcon={<IconDeviceFloppy size={"1.4rem"} />}
              variant="outline"
              loading={isLoading}>
              Save
            </Button>
          </ButtonsWrapper>
        </Breadcrumbs>
        <Flex justify="center">
          <LayoutContainer style={{ width: "100%", maxWidth: 600 }}>
            <Section style={{ alignItems: "normal" }}>
              <SectionDivider mr="auto" mb={30}>
                Enable UN-LOCODE Automation for Logbooks
              </SectionDivider>
              <CheckboxGroup
                name="logbooks"
                control={control}
                scrollHeight={500}
                data={data?.selects?.logbooks ?? []}
                searchable
                full
                loading={lbLoading}
              />
            </Section>
          </LayoutContainer>
        </Flex>
      </form>
    </PageContainer>
  );
};

export default UnCodeAutomation;
